import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { API } from "../lib/api";

// Format the raw input into `GAF-XXXX-XXXX` as the user types. Strips
// anything that isn't A-Z0-9, auto-uppercases, and slices to 11 body
// chars max (GAF prefix + 8 unique chars) so the display stays neat.
function formatCode(raw) {
    let s = (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    // Ensure GAF prefix — if user typed the middle only, we still handle it
    if (!s.startsWith("GAF")) {
        // Only auto-prefix if it looks like they omitted it; otherwise let
        // them type freely (they may be pasting partial). Do nothing here.
    }
    // Split into groups: GAF - 4 - 4
    const body = s.startsWith("GAF") ? s.slice(3) : s;
    const prefix = s.startsWith("GAF") ? "GAF" : s.slice(0, 3);
    const part1 = body.slice(0, 4);
    const part2 = body.slice(4, 8);
    if (!s.startsWith("GAF") && s.length <= 3) return prefix;
    if (!part1) return prefix ? `${prefix}-` : "";
    if (!part2) return `${prefix}-${part1}`;
    return `${prefix}-${part1}-${part2}`;
}

// Human-friendly date rendering for the expiry line.
function formatExpiry(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString("en-AU", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return null;
    }
}

const STATUS_META = {
    active: {
        label: "Active",
        tone: "bg-[#E6F4EA] text-[#166534] border-[#166534]/20",
        headline: "left to spend",
    },
    used: {
        label: "Fully used",
        tone: "bg-[#F3F1EC] text-[#6B6B6B] border-black/10",
        headline: "remaining",
    },
    expired: {
        label: "Expired",
        tone: "bg-[#FBEEE6] text-[#8B4513] border-[#8B4513]/20",
        headline: "remaining",
    },
    voided: {
        label: "Voided",
        tone: "bg-[#FBE9E9] text-[#B42318] border-[#B42318]/20",
        headline: "remaining",
    },
};

export default function VouchersBalancePage() {
    const [params, setParams] = useSearchParams();
    const [code, setCode] = useState(formatCode(params.get("code") || ""));
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    // Deep-link auto-lookup: /vouchers/balance?code=GAF-XXXX-XXXX
    useEffect(() => {
        const q = params.get("code");
        if (q) {
            const normalised = formatCode(q);
            if (normalised.length >= 12) {
                doLookup(normalised);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function doLookup(rawCode) {
        setLoading(true);
        setError("");
        setResult(null);
        // Keep hyphens — DB stores codes as GAF-XXXX-XXXX.
        const clean = (rawCode || "").toUpperCase().trim();
        try {
            const r = await fetch(
                `${API}/api/vouchers/lookup/${encodeURIComponent(clean)}`
            );
            if (r.status === 404) {
                setError(
                    "Hmm, we couldn't find that code. Double-check the format — it should look like GAF-XXXX-XXXX."
                );
                return;
            }
            if (!r.ok) throw new Error("Lookup failed");
            const data = await r.json();
            setResult(data);
        } catch (e) {
            setError(
                "Something went wrong looking that up. Give it another go in a moment."
            );
        } finally {
            setLoading(false);
        }
    }

    function onSubmit(e) {
        e.preventDefault();
        if (!code.trim()) return;
        setParams({ code });
        doLookup(code);
    }

    return (
        <section
            data-testid="vouchers-balance-page"
            className="min-h-[80vh] bg-[#FAF8F4] pt-32 md:pt-40 pb-24"
        >
            <div className="mx-auto max-w-[720px] px-6 md:px-10 lg:px-14">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
                >
                    <p className="overline mb-3">Check your voucher</p>
                    <h1 className="font-display uppercase text-[clamp(2.4rem,6.5vw,5rem)] leading-[0.92] text-[#121212]">
                        How much
                        <br />
                        <span className="text-[#EB4A18]">is left?</span>
                    </h1>
                    <p className="mt-6 max-w-xl text-base md:text-lg text-[#3A3A3A] leading-relaxed">
                        Type your code below to see the remaining balance,
                        expiry date and status. Nothing personal is shown —
                        only what's on the voucher.
                    </p>
                </motion.div>

                <form
                    onSubmit={onSubmit}
                    className="mt-12 space-y-4"
                    data-testid="balance-lookup-form"
                >
                    <label className="block text-[0.65rem] uppercase tracking-[0.22em] font-bold text-[#6B6B6B] mb-2">
                        Voucher code
                    </label>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <input
                            type="text"
                            data-testid="balance-code-input"
                            value={code}
                            onChange={(e) =>
                                setCode(formatCode(e.target.value))
                            }
                            placeholder="GAF-XXXX-XXXX"
                            autoCapitalize="characters"
                            autoCorrect="off"
                            spellCheck="false"
                            className="flex-1 p-4 border-2 border-[#121212]/15 focus:border-[#EB4A18] outline-none bg-white text-[#121212] font-mono tracking-[0.12em] uppercase"
                        />
                        <button
                            type="submit"
                            data-testid="balance-check-btn"
                            disabled={!code.trim() || loading}
                            className="btn-primary text-sm px-8 py-4 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                            {loading ? "Checking…" : "Check balance"}
                            <span aria-hidden="true">→</span>
                        </button>
                    </div>
                </form>

                {/* Error state */}
                {error && (
                    <motion.div
                        data-testid="balance-error"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-8 p-5 bg-white border-l-4 border-[#EB4A18] text-[#3A3A3A] text-sm md:text-base leading-relaxed"
                    >
                        {error}
                    </motion.div>
                )}

                {/* Result card */}
                {result && (
                    <motion.div
                        data-testid="balance-result"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
                        className="mt-10"
                    >
                        <div className="bg-[#121212] text-[#FAF8F4] p-8 md:p-10">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="overline !text-[#EB4A18] mb-2">
                                        {STATUS_META[result.status]?.headline ||
                                            "remaining"}
                                    </p>
                                    <p
                                        data-testid="balance-remaining"
                                        className="font-display text-6xl md:text-7xl leading-none"
                                    >
                                        ${Number(result.amount_remaining).toFixed(2)}
                                    </p>
                                </div>
                                <span
                                    data-testid="balance-status-badge"
                                    className={`text-[0.65rem] uppercase tracking-[0.22em] font-bold px-3 py-1.5 border ${
                                        STATUS_META[result.status]?.tone ||
                                        "bg-[#F3F1EC] text-[#6B6B6B] border-black/10"
                                    }`}
                                >
                                    {STATUS_META[result.status]?.label ||
                                        result.status}
                                </span>
                            </div>

                            <div className="mt-8 pt-6 border-t border-white/15 grid grid-cols-2 gap-6 text-xs md:text-sm">
                                <div>
                                    <p className="overline text-white/50 mb-1">
                                        Voucher code
                                    </p>
                                    <p className="font-mono tracking-[0.12em]">
                                        {result.code}
                                    </p>
                                </div>
                                <div>
                                    <p className="overline text-white/50 mb-1">
                                        Original amount
                                    </p>
                                    <p>
                                        ${Number(result.amount_initial).toFixed(
                                            2
                                        )} AUD
                                    </p>
                                </div>
                                {formatExpiry(result.expires_at) && (
                                    <div className="col-span-2">
                                        <p className="overline text-white/50 mb-1">
                                            {result.status === "expired"
                                                ? "Expired on"
                                                : "Valid until"}
                                        </p>
                                        <p>
                                            {formatExpiry(result.expires_at)}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <p className="mt-6 text-sm text-[#6B6B6B] leading-relaxed">
                            Show this code at the counter — 190 York St,
                            Albany. Unused balance stays on the voucher for the
                            next visit.
                        </p>
                    </motion.div>
                )}

                {/* Helpful footer links */}
                <div className="mt-16 pt-8 border-t border-black/10 flex flex-wrap gap-6 text-xs uppercase tracking-[0.22em] text-[#6B6B6B]">
                    <Link
                        to="/vouchers"
                        data-testid="balance-buy-link"
                        className="hover:text-[#EB4A18] transition-colors"
                    >
                        Buy a voucher →
                    </Link>
                    <Link
                        to="/book"
                        className="hover:text-[#EB4A18] transition-colors"
                    >
                        Book a table →
                    </Link>
                </div>
            </div>
        </section>
    );
}
